import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supportAccessApi } from '@/api/kyc.api';
import { useAuth } from '@/auth/useAuth';
import { humanizeError } from '@/utils/errorMessage';

/** Marks this tab as a support session, for the banner in the workspace. */
export const SUPPORT_SESSION_KEY = 'travelerp.supportSession';

/**
 * Completes a support sign-in (Module 0, O13): exchanges the single-use code super admin was given — valid only
 * while the company has allowed support access — for a session in the company's workspace.
 */
export default function SupportLoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const code = params.get('code') ?? '';
    (async () => {
      try {
        const result = await supportAccessApi.login(code);
        await adopt(result);
        try { window.sessionStorage.setItem(SUPPORT_SESSION_KEY, '1'); } catch { /* private mode */ }
        navigate('/dashboard', { replace: true });
      } catch (e) {
        setError(humanizeError(e, 'This support link is no longer valid.'));
      }
    })();
  }, [adopt, navigate, params]);

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', px: 2, bgcolor: 'background.default' }}>
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 440, textAlign: 'center' }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
            <Button variant="outlined" onClick={() => window.close()}>Close this tab</Button>
          </>
        ) : (
          <>
            <CircularProgress />
            <Typography>Signing in as TravelERP support…</Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
